import axios from 'axios';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { useAuthStore } from '@/stores/authStore';
import router from '@/router';
import { useSystemStore } from '@/stores/systemStore';


const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 40000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});


const isNative = Capacitor.isNativePlatform();

function handleUnauthorized() {
  const authStore = useAuthStore();
  authStore.logout?.();

  if (router.currentRoute.value.path !== '/sign-in') {
    router.push({ path: '/sign-in' });
  }
}


async function request(config: any) {
  const authStore = useAuthStore();
  
  // console.log('--- AXIOS REQUEST DEBUG ---');
  // console.log('URL:', config.url);
  // console.log('Method:', config.method);
  // console.log('Base URL:', apiClient.defaults.baseURL);
  // console.log('Is Native:', isNative);
  // console.log('Auth Token exits:', !!authStore.token);
  // console.log('NODE_ENV:', process.env.NODE_ENV);
  
  if (process.env.NODE_ENV === 'development' && authStore.token) {
    console.log('Adding Bearerization header');
    config.headers = {
      ...config.headers,
      Bearerization: `Bearer ${authStore.token}`,
    };
  }
  
  console.log('Final Headers:', JSON.stringify(config.headers));

  if (isNative) {
    try {
      const method = (config.method || 'GET').toUpperCase();
      const methodAllowsBody = !['GET', 'HEAD', 'DELETE'].includes(method);

      const nativeRequest: any = {
        method,
        url: `${process.env.VUE_APP_API_URL}${config.url}`,
        headers: config.headers || { 'Content-Type': 'application/json' },
        params: config.params || {},
        // Force JSON parsing on the native side. Without an explicit
        // responseType iOS may fail with NSURLErrorDataLengthExceedsMaximum
        // (-1103, "resource exceeds maximum size").
        responseType: 'json',
      };

      // iOS rejects GET/HEAD/DELETE requests that include a body
      // ("GET method must not have a body"). Only attach data when
      // the method actually allows a body.
      if (methodAllowsBody && config.data !== undefined) {
        nativeRequest.data = config.data;
      }

      const response = await CapacitorHttp.request(nativeRequest);

      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (err) {
          console.warn('Failed to parse native response JSON', err);          
        }
      }

      if (response.status >= 400) {
        const message =
          data?.['hydra:description'] ||
          data?.message ||
          data?.error ||
          `Request failed with status ${response.status}`;

        const error: any = new Error(message);

        error.response = {
          status: response.status,
          data,
        };

        error.isAxiosError = true;   
        error.name = 'AxiosError'; 
        error.config = config; 
        error.toJSON = () => ({ message }); 

        if (response.status === 401) handleUnauthorized();

        throw error;
      }

      return {
        data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error: any) {
      if (error.status === 401) handleUnauthorized();
      throw error;
    }
  } else {
    try {
      const response = await apiClient(config); 
      
      return response;
    } catch (error: any) {
      console.error(`--- RESPONSE ERROR [${config.url}] ---`, error);
      if (error.code === 'ECONNABORTED') {
          console.error('[AXIOS] Request timed out completely');
      }
      if (error.response) {
         console.error('Status:', error.response.status);
         console.error('Data:', error.response.data);
      } else {
         console.error('[AXIOS] No Response Object received');
      }
     
        const systemStore = useSystemStore();
        if (!error.response) {
          console.error('Backend connection failed:', error.message);
          systemStore.activateMaintenance();
        } else if (
          error.code === 'ECONNABORTED' ||
          error.message?.toLowerCase().includes('network error')
        ) {
          console.error('Network error or timeout');
          systemStore.activateMaintenance();
        } else if (error.response.status >= 500) {
          console.error('Backend returned 5xx error:', error.response.status);
          systemStore.activateMaintenance();
        } else if (error.response.status === 401 && !config?.skipAuthLogout) {
          handleUnauthorized();
        }

        throw error;
      }
  }
}

export default {
  get<T = any>(url: string, config = {}): Promise<{ data: T; status: number; headers: any }> {
    return request({ ...config, method: 'GET', url });
  },
  post<T = any>(url: string, data = {}, config = {}): Promise<{ data: T; status: number; headers: any }> {
    return request({ ...config, method: 'POST', url, data });
  },
  put<T = any>(url: string, data = {}, config = {}): Promise<{ data: T; status: number; headers: any }> {
    return request({ ...config, method: 'PUT', url, data });
  },
  delete<T = any>(url: string, config = {}): Promise<{ data: T; status: number; headers: any }> {
    return request({ ...config, method: 'DELETE', url });
  },
};